// Pseudo-legal move generation: every move the pieces can physically make,
// before the king-safety filter (legalMoves.ts) and without castling, which
// depends on attacked squares and lives with the legality code.
import {
  BISHOP, EMPTY, FLAG_CAPTURE, FLAG_DOUBLE_PUSH, FLAG_EN_PASSANT, FLAG_NONE, FLAG_PROMOTION,
  KING, KNIGHT, NO_SQUARE, PAWN, PROMOTION_TYPES, QUEEN, ROOK,
  isColor, typeOf, type Color, type Move, type Position, type Square,
} from './types.js';
import { BOARD_SIZE, SQUARE_COUNT, fileOf, rowOf, squareAt } from './squares.js';

/** [fileDelta, rowDelta] pairs. Row grows toward rank 1. */
export type Delta = readonly [number, number];

export const KNIGHT_DELTAS: readonly Delta[] = [[1, 2], [2, 1], [2, -1], [1, -2], [-1, -2], [-2, -1], [-2, 1], [-1, 2]];
export const KING_DELTAS: readonly Delta[] = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
export const ROOK_DIRS: readonly Delta[] = [[1, 0], [-1, 0], [0, 1], [0, -1]];
export const BISHOP_DIRS: readonly Delta[] = [[1, 1], [1, -1], [-1, 1], [-1, -1]];
export const QUEEN_DIRS: readonly Delta[] = [...ROOK_DIRS, ...BISHOP_DIRS];

const WHITE_PAWN_START_ROW = 6;
const BLACK_PAWN_START_ROW = 1;

/** Row a pawn of `color` advances toward: -1 for white (to rank 8), +1 for black. */
export function pawnDirection(color: Color): number {
  return color === 'w' ? -1 : 1;
}

/** Row on which a pawn of `color` promotes. */
export function promotionRow(color: Color): number {
  return color === 'w' ? 0 : BOARD_SIZE - 1;
}

export function makeMove(from: Square, to: Square, piece: number, captured: number, flags: number, promotion: number = EMPTY): Move {
  return { from, to, piece, captured, promotion, flags };
}

function addPawnAdvance(out: Move[], from: Square, to: Square, piece: number, captured: number, flags: number, color: Color): void {
  if (rowOf(to) !== promotionRow(color)) {
    out.push(makeMove(from, to, piece, captured, flags));
    return;
  }
  for (const type of PROMOTION_TYPES) out.push(makeMove(from, to, piece, captured, flags | FLAG_PROMOTION, type));
}

function generatePawn(pos: Position, from: Square, piece: number, out: Move[]): void {
  const color = pos.turn;
  const dir = pawnDirection(color);
  const file = fileOf(from);
  const row = rowOf(from);

  const one = squareAt(file, row + dir);
  if (one !== NO_SQUARE && pos.board[one] === EMPTY) {
    addPawnAdvance(out, from, one, piece, EMPTY, FLAG_NONE, color);
    const startRow = color === 'w' ? WHITE_PAWN_START_ROW : BLACK_PAWN_START_ROW;
    const two = squareAt(file, row + 2 * dir);
    if (row === startRow && two !== NO_SQUARE && pos.board[two] === EMPTY) {
      out.push(makeMove(from, two, piece, EMPTY, FLAG_DOUBLE_PUSH));
    }
  }

  for (const df of [-1, 1]) {
    const to = squareAt(file + df, row + dir);
    if (to === NO_SQUARE) continue;
    const target = pos.board[to] ?? EMPTY;
    if (target !== EMPTY && !isColor(target, color)) {
      addPawnAdvance(out, from, to, piece, target, FLAG_CAPTURE, color);
    } else if (to === pos.epSquare) {
      out.push(makeMove(from, to, piece, -piece, FLAG_CAPTURE | FLAG_EN_PASSANT));
    }
  }
}

function generateSteps(pos: Position, from: Square, piece: number, deltas: readonly Delta[], out: Move[]): void {
  const file = fileOf(from);
  const row = rowOf(from);
  for (const [df, dr] of deltas) {
    const to = squareAt(file + df, row + dr);
    if (to === NO_SQUARE) continue;
    const target = pos.board[to] ?? EMPTY;
    if (target === EMPTY) out.push(makeMove(from, to, piece, EMPTY, FLAG_NONE));
    else if (!isColor(target, pos.turn)) out.push(makeMove(from, to, piece, target, FLAG_CAPTURE));
  }
}

function generateSlides(pos: Position, from: Square, piece: number, dirs: readonly Delta[], out: Move[]): void {
  const file = fileOf(from);
  const row = rowOf(from);
  for (const [df, dr] of dirs) {
    for (let step = 1; ; step++) {
      const to = squareAt(file + df * step, row + dr * step);
      if (to === NO_SQUARE) break;
      const target = pos.board[to] ?? EMPTY;
      if (target === EMPTY) {
        out.push(makeMove(from, to, piece, EMPTY, FLAG_NONE));
        continue;
      }
      if (!isColor(target, pos.turn)) out.push(makeMove(from, to, piece, target, FLAG_CAPTURE));
      break;
    }
  }
}

/** Pseudo-legal moves for the side to move, castling excluded. */
export function generatePseudoLegal(pos: Position): Move[] {
  const out: Move[] = [];
  for (let sq = 0; sq < SQUARE_COUNT; sq++) {
    const piece = pos.board[sq] ?? EMPTY;
    if (piece === EMPTY || !isColor(piece, pos.turn)) continue;
    switch (typeOf(piece)) {
      case PAWN: generatePawn(pos, sq, piece, out); break;
      case KNIGHT: generateSteps(pos, sq, piece, KNIGHT_DELTAS, out); break;
      case KING: generateSteps(pos, sq, piece, KING_DELTAS, out); break;
      case BISHOP: generateSlides(pos, sq, piece, BISHOP_DIRS, out); break;
      case ROOK: generateSlides(pos, sq, piece, ROOK_DIRS, out); break;
      case QUEEN: generateSlides(pos, sq, piece, QUEEN_DIRS, out); break;
    }
  }
  return out;
}
